import {
  EMPTY_REPORT_CANVAS_LAYOUT,
  type ReportCanvasLayout,
  type ReportFilter,
} from '@erp/shared-types';
import type { WorkbookReportHeadingValue } from './WorkbookReportHeading';

/** A Report's settings besides its layout and heading: its colors and filters. */
export type WorkbookReportOptions = {
  paletteId?: string;
  filters?: ReportFilter[];
};

/** What the Report's edit history holds: its layout, heading, colors and filters. */
export type ReportContent = {
  layout: ReportCanvasLayout;
} & WorkbookReportHeadingValue &
  WorkbookReportOptions;

/** The Report's content as saved; an absent layout is the empty one. */
export function reportContent(
  layout: ReportCanvasLayout | undefined,
  heading: WorkbookReportHeadingValue | undefined,
  options: WorkbookReportOptions | undefined,
): ReportContent {
  return {
    layout: layout ?? EMPTY_REPORT_CANVAS_LAYOUT,
    title: heading?.title,
    description: heading?.description,
    paletteId: options?.paletteId,
    filters: options?.filters,
  };
}

/** Same colors and filters; an absent palette or filter list is the default. */
export function sameOptions(
  a: WorkbookReportOptions,
  b: WorkbookReportOptions,
): boolean {
  return (
    (a.paletteId ?? '') === (b.paletteId ?? '') &&
    JSON.stringify(a.filters ?? []) === JSON.stringify(b.filters ?? [])
  );
}

/** Same content, as the edit history compares its steps; absent text is blank. */
export function sameReportContent(a: ReportContent, b: ReportContent): boolean {
  return (
    (a.title ?? '') === (b.title ?? '') &&
    (a.description ?? '') === (b.description ?? '') &&
    sameOptions(a, b) &&
    JSON.stringify(a.layout) === JSON.stringify(b.layout)
  );
}

/** Which parts of the saved Report a history step moves back to differ from what is saved. */
export function changedReportParts(
  state: ReportContent,
  saved: ReportContent,
): { layout: boolean; heading: boolean; options: boolean } {
  return {
    layout: JSON.stringify(state.layout) !== JSON.stringify(saved.layout),
    heading:
      state.title !== saved.title || state.description !== saved.description,
    options: !sameOptions(state, saved),
  };
}

/** A title as a reader compares it: no Markdown emphasis, case or extra spaces. */
function comparableTitle(text: string): string {
  return text
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** The component at the Report's top left, which a reader meets first. */
function leadingComponent(layout: ReportCanvasLayout | undefined) {
  if (!layout) return undefined;
  if ('data' in layout) return layout.data[0]?.[0];
  const [first] = [...layout.items].sort((a, b) => a.y - b.y || a.x - b.x);
  return first?.component;
}

/**
 * What of the Report's heading its content already shows: the title when the
 * content opens with a text block headed by it, as a generated Report often
 * does, and the description too when that block goes on past its heading, so
 * a heading above it would repeat them.
 */
export function layoutRepeatsHeading(
  layout: ReportCanvasLayout | undefined,
  title: string | undefined,
): { title: boolean; description: boolean } {
  const none = { title: false, description: false };
  const wanted = comparableTitle(title ?? '');
  if (!wanted) return none;
  const component = leadingComponent(layout);
  if (component?.type !== 'text' || typeof component.markdown !== 'string')
    return none;
  const lines = component.markdown.split('\n').filter((line) => line.trim());
  const heading = lines[0]?.match(/^ {0,3}#{1,6}\s+(.*?)(?:\s+#+)?\s*$/);
  if (heading?.[1] === undefined || comparableTitle(heading[1]) !== wanted)
    return none;
  return { title: true, description: lines.length > 1 };
}

/**
 * The Report's heading less what its content already shows, so viewing it
 * shows each once: its title and description stay unless repeated.
 */
export function unrepeatedHeading<
  T extends { title?: string; description?: string },
>(heading: T, layout: ReportCanvasLayout | undefined): T {
  const repeated = layoutRepeatsHeading(layout, heading.title);
  return {
    ...heading,
    ...(repeated.title && { title: undefined }),
    ...(repeated.description && { description: undefined }),
  };
}
